import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useSidebar } from '@/context/sidebar';

export interface SidebarLink {
  to: string;
  label: string;
  icon?: React.ReactNode;
  subItems?: Array<{ to: string; label: string }>;
}

interface SidebarProps {
  links: SidebarLink[];
  className?: string;
}

/**
 * Composant Sidebar avec menu dépliable
 * Design : Dark Theme (Slate 900) pour cohérence avec Login
 */
export function Sidebar({ links, className }: SidebarProps) {
  const { isExpanded, isHovered, isMobileOpen, setIsHovered, setIsMobileOpen } = useSidebar();
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
  const shouldUseExactMatch = (path: string) => path.split('/').filter(Boolean).length === 1;
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : false
  );

  const isOpen = isExpanded || isHovered || (!isDesktop && isMobileOpen);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isDesktop) setIsMobileOpen(false);
  }, [isDesktop, setIsMobileOpen]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname, setIsMobileOpen]);

  useEffect(() => {
    links.forEach((link, index) => {
      if (link.subItems) {
        const isActive = link.subItems.some(sub => location.pathname === sub.to);
        if (isActive) setOpenSubmenu(index);
      }
    });
  }, [location.pathname, links]);

  const handleSubmenuToggle = (index: number) => {
    setOpenSubmenu(prev => prev === index ? null : index);
  };

  const isDrawerOpen = isMobileOpen && !isDesktop;
  const desktopWidth = isExpanded || isHovered ? 'w-72' : 'w-20';

  return (
    <>
      {/* Backdrop mobile */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-overlay"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed flex flex-col bg-slate-900 border-r border-slate-800 shadow-2xl overflow-hidden',
          'transition-all duration-300 ease-in-out z-sidebar',
          isDesktop
            ? cn(
                desktopWidth,
                'top-24 left-4 bottom-4 rounded-3xl', 
                'md:translate-x-0'
              )
            : cn(
                'top-0 left-0 h-full w-full max-w-[280px] rounded-r-3xl rounded-l-none',
                isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
              ),
          className
        )}
        onMouseEnter={() => isDesktop && !isExpanded && setIsHovered(true)}
        onMouseLeave={() => isDesktop && setIsHovered(false)}
      >
        {/* Header mobile */}
        {!isDesktop && (
          <div className="flex items-center justify-between px-6 py-6 border-b border-slate-800">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase text-blue-400 tracking-[0.2em] mb-1">Menu</span>
              <span className="text-lg font-bold text-white tracking-wider">ASTÉROÏDE</span>
            </div>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6L18 18"/></svg>
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav
          className={cn(
            'flex-1 overflow-y-auto px-3 pb-6 custom-scrollbar',
            isDesktop ? 'pt-6' : 'pt-4'
          )}
        >
          <ul className="space-y-1.5">
            {links.map((link, index) => {
              const useExactMatch = shouldUseExactMatch(link.to);
              return (
                <li key={link.to}>
                {link.subItems ? (
                  // Submenu item
                  <div>
                    <button
                      onClick={() => handleSubmenuToggle(index)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 group relative overflow-hidden',
                        openSubmenu === index 
                          ? 'bg-slate-800 text-white'
                          : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200',
                        !isOpen ? 'justify-center' : 'justify-start'
                      )}
                    >
                      {link.icon && (
                        <span className={cn('text-xl transition-colors shrink-0', openSubmenu === index ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300')}>
                          {link.icon}
                        </span>
                      )}
                      {isOpen && (
                        <>
                          <span className="flex-1 text-left truncate">{link.label}</span>
                          <span
                            className={cn(
                              'text-[10px] transition-transform duration-200 text-slate-500',
                              openSubmenu === index ? 'rotate-180' : ''
                            )}
                          >
                            ▼
                          </span>
                        </>
                      )}
                    </button>
                    
                    {/* Submenu */}
                    {isOpen && link.subItems && (
                      <div
                        className={cn(
                          'overflow-hidden transition-all duration-300',
                          openSubmenu === index ? 'max-h-96 mt-1' : 'max-h-0'
                        )}
                      >
                        <div className="ml-4 pl-3 border-l border-slate-800 space-y-1 my-1">
                            {link.subItems.map(subItem => (
                              <NavLink
                                key={subItem.to}
                                to={subItem.to}
                                className={({ isActive }) =>
                                  cn(
                                    'block px-3 py-2 rounded-lg text-sm transition-all duration-200',
                                    isActive
                                      ? 'text-white bg-blue-600/10 border border-blue-600/20 shadow-sm'
                                      : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/30'
                                  )
                                }
                              >
                                {subItem.label}
                              </NavLink>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Simple link
                  <NavLink to={link.to} end={useExactMatch}>
                    {({ isActive }) => {
                       // Custom active logic if needed, but NavLink handles most
                       return (
                        <div
                          className={cn(
                            'flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 group relative overflow-hidden',
                            isActive
                              ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20 ring-1 ring-white/10'
                              : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200',
                            !isOpen ? 'justify-center' : 'justify-start'
                          )}
                        >
                          {/* Active Indicator Glow */}
                          {isActive && <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 opacity-100 z-[-1]" />}

                          {link.icon && (
                            <span
                              className={cn(
                                'text-xl transition-colors shrink-0',
                                isActive ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'
                              )}
                            >
                              {link.icon}
                            </span>
                          )}
                          {isOpen && <span className="truncate">{link.label}</span>}
                        </div>
                      );
                    }}
                  </NavLink>
                )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Mini Profile (Desktop Footer) */}
        {isDesktop && isOpen && (
           <div className="p-4 border-t border-slate-800 bg-slate-950/30">
              <div className="flex items-center gap-3">
                 <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-white border border-slate-700">
                    AP
                 </div>
                 <div className="flex flex-col overflow-hidden">
                    <span className="text-xs font-bold text-white truncate">Astéroïde</span>
                    <span className="text-[10px] text-slate-500 truncate">Production</span>
                 </div>
              </div>
           </div>
        )}
      </aside>
    </>
  );
}
